import type { PopoverContentProps } from "@radix-ui/react-popover";
import IndicatorCategoryDot from "components/financials/IndicatorCategoryDot";
import type { IIndicatorFieldsFragment } from "components/financials/modern/graphql/fragments/__generated__/IndicatorFieldsFragment.generated";
import getIndicatorDocumentation from "components/financials/modern/indicators/getIndicatorDocumentation";
import { Popover, PopoverContent } from "components/ui/Popover";
import classNames from "components/ui/classNames";
import useTheme from "components/ui/useTheme";
import type { ReactNode } from "react";
import { useIntl } from "react-intl";
import tinycolor from "tinycolor2";

interface IndicatorDocumentationPopoverProps {
  side?: PopoverContentProps["side"];
  sideOffset?: PopoverContentProps["sideOffset"];

  children: ReactNode;
  indicator: Pick<IIndicatorFieldsFragment, "objectId" | "documentation" | "columnTheme">;
}
export default function IndicatorDocumentationPopover({
  side,
  sideOffset,
  indicator,
  children,
}: IndicatorDocumentationPopoverProps) {
  const intl = useIntl();
  const theme = useTheme();
  const isDarkTheme = theme === "dark";
  const documentation = getIndicatorDocumentation(intl, indicator);

  return (
    <Popover>
      {children}

      <PopoverContent
        side={side || "top"}
        sideOffset={sideOffset || 12}
        hideWhenDetached={true}
        avoidCollisions={true}
        onOpenAutoFocus={(event) => {
          // See: https://github.com/radix-ui/primitives/discussions/935
          event.preventDefault();
        }}
        className={classNames(
          "w-sm space-y-2 overflow-hidden rounded-xl border border-slate-300 p-2",
          "shadow-lg shadow-slate-900/10",
          {
            "border-slate-900/80 bg-slate-900 text-slate-300 shadow-neutral-950/40": theme === "dark",
            dark: theme === "dark",
          },
        )}
      >
        <div className="p-2">
          <div className="space-y-2">
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <IndicatorCategoryDot indicator={indicator} />
                <h1
                  className={classNames("text-sm font-bold")}
                  style={{
                    color: isDarkTheme
                      ? tinycolor(indicator.columnTheme.backgroundColor).darken(20).toRgbString()
                      : indicator.columnTheme.textColor,
                  }}
                >
                  {documentation.abbreviation}
                </h1>
              </div>
              {documentation.name !== documentation.abbreviation && (
                <h2 className={classNames("text-slate line-clamp-2 text-sm font-bold italic text-slate-600")}>
                  {documentation.name}
                </h2>
              )}
            </div>
            <p
              className={classNames(
                "max-w-xs text-xs font-medium tracking-wider text-slate-500",
                "dark:text-slate-400",
              )}
            >
              {documentation.description}
            </p>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
